import { SupabaseClient } from '@supabase/supabase-js';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DuplicateMatch {
  id: string;
  empresa_id: string;
  nombre_archivo: string;
  numero_factura: string | null;
  nif_emisor: string | null;
  total_factura: number | null;
  fecha_factura: string | null;
  tipo_documento: string | null;
  estado: string | null;
}

export interface DuplicateResult {
  exactMatches: DuplicateMatch[];
  potentialMatches: DuplicateMatch[];
  confidence: 'none' | 'low' | 'medium' | 'high';
}

interface DocumentFields {
  empresa_id: string;
  numero_factura?: string | null;
  nif_emisor?: string | null;
  total_factura?: number | null;
  fecha_factura?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function computeConfidence(exact: number, potential: number): DuplicateResult['confidence'] {
  if (exact > 0) return 'high';
  if (potential >= 3) return 'high';
  if (potential >= 1) return 'medium';
  return 'none';
}

/**
 * Adds/subtracts `days` from a date string (YYYY-MM-DD or ISO).
 */
function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// ─── Core Functions ──────────────────────────────────────────────────────────

/**
 * Find exact duplicate documents: same (nif_emisor + total + fecha + numero_factura)
 * within the same empresa.
 */
export async function findDuplicates(
  supabase: SupabaseClient,
  documentId: string
): Promise<DuplicateResult> {
  // 1. Fetch the source document
  const { data: doc, error: docError } = await supabase
    .from('documentos_financieros')
    .select('id, empresa_id, numero_factura, nif_emisor, total_factura, fecha_factura')
    .eq('id', documentId)
    .single();

  if (docError || !doc) {
    throw new Error(`Documento no encontrado: ${docError?.message ?? documentId}`);
  }

  const fields: DocumentFields = {
    empresa_id: doc.empresa_id,
    numero_factura: doc.numero_factura,
    nif_emisor: doc.nif_emisor,
    total_factura: doc.total_factura,
    fecha_factura: doc.fecha_factura,
  };

  return findDuplicatesByFields(supabase, fields, documentId);
}

/**
 * Find duplicates by raw field values (no need for an existing document).
 * `excludeId` prevents matching the source document itself.
 */
export async function findDuplicatesByFields(
  supabase: SupabaseClient,
  fields: DocumentFields,
  excludeId?: string
): Promise<DuplicateResult> {
  const { empresa_id, numero_factura, nif_emisor, total_factura, fecha_factura } = fields;

  // ── Exact matches ────────────────────────────────────────────────────────
  let exactMatches: DuplicateMatch[] = [];

  if (numero_factura && nif_emisor && total_factura != null && fecha_factura) {
    let query = supabase
      .from('documentos_financieros')
      .select('id, empresa_id, nombre_archivo, numero_factura, nif_emisor, total_factura, fecha_factura, tipo_documento, estado')
      .eq('empresa_id', empresa_id)
      .eq('numero_factura', numero_factura)
      .eq('nif_emisor', nif_emisor)
      .eq('total_factura', total_factura)
      .eq('fecha_factura', fecha_factura);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[duplicate-detector] Error finding exact matches:', error);
    } else {
      exactMatches = (data ?? []) as DuplicateMatch[];
    }
  }

  // ── Potential (fuzzy) matches ────────────────────────────────────────────
  const potentialMatches = await findPotentialDuplicates(
    supabase,
    fields,
    excludeId,
    exactMatches.map(m => m.id)
  );

  return {
    exactMatches,
    potentialMatches,
    confidence: computeConfidence(exactMatches.length, potentialMatches.length),
  };
}

/**
 * Fuzzy duplicate search: amount ±5 %, date ±3 days, same nif_emisor within empresa.
 * Excludes IDs already returned as exact matches.
 */
export async function findPotentialDuplicates(
  supabase: SupabaseClient,
  fields: DocumentFields,
  excludeId?: string,
  excludeIds: string[] = []
): Promise<DuplicateMatch[]> {
  const { empresa_id, nif_emisor, total_factura, fecha_factura } = fields;

  // Need at least nif_emisor + one of (total or fecha) for a meaningful fuzzy search
  if (!nif_emisor || (total_factura == null && !fecha_factura)) {
    return [];
  }

  let query = supabase
    .from('documentos_financieros')
    .select('id, empresa_id, nombre_archivo, numero_factura, nif_emisor, total_factura, fecha_factura, tipo_documento, estado')
    .eq('empresa_id', empresa_id)
    .eq('nif_emisor', nif_emisor);

  // Amount ±5 %
  if (total_factura != null) {
    const margin = Math.abs(total_factura) * 0.05;
    const low = total_factura - margin;
    const high = total_factura + margin;
    query = query.gte('total_factura', low).lte('total_factura', high);
  }

  // Date ±3 days
  if (fecha_factura) {
    const from = shiftDate(fecha_factura, -3);
    const to = shiftDate(fecha_factura, 3);
    query = query.gte('fecha_factura', from).lte('fecha_factura', to);
  }

  // Exclude self
  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[duplicate-detector] Error finding potential matches:', error);
    return [];
  }

  // Filter out IDs already counted as exact
  const allExclude = new Set(excludeIds);
  return ((data ?? []) as DuplicateMatch[]).filter(d => !allExclude.has(d.id));
}
